import albumsData from '@/data/albums.json';
import galleryData from '@/data/gallery.json';

export interface Photo {
  id: number;
  src: string;
  alt: string;
  width: number;
  height: number;
  blurColor: string;
  album: string;
}

export interface Album {
  slug: string;
  title: string;
  description: string;
  /**
   * `AAAA-MM-JJ` quand le jour est connu, `AAAA-MM` quand seul le mois l'est,
   * null quand la date reste à confirmer. Rien ne s'affiche dans ce cas :
   * mieux vaut pas de date qu'une date inventée.
   */
  date: string | null;
  /**
   * Année scolaire imposée, quand on sait à quelle année appartient l'album
   * sans en connaître le jour exact (ex. « 2023-2024 »). Sert au filtre.
   * Ignorée si `date` est renseignée, qui est alors plus précise.
   */
  schoolYear?: string | null;
  /** Événement correspondant dans events.json, pour lier les deux pages. */
  eventSlug: string | null;
  /** Identifiant de la photo de couverture. */
  cover: number;
}

export interface AlbumWithPhotos extends Album {
  photos: Photo[];
  coverPhoto: Photo;
  /** Année scolaire déduite de la date, ex. "2025-2026". null si non datée. */
  schoolYear: string | null;
}

const photos = galleryData as Photo[];
const albums = albumsData as Album[];

/**
 * Année scolaire à laquelle appartient une date : au Togo comme en France,
 * elle démarre en septembre. Une photo de janvier 2026 relève donc de
 * l'année 2025-2026.
 */
/**
 * Met en forme une date d'album selon sa précision : « 24 juillet 2026 » quand
 * le jour est connu, « janvier 2026 » quand seul le mois l'est. On n'affiche
 * jamais un jour qu'on ne connaît pas.
 */
export function formatAlbumDate(date: string): string {
  const hasDay = /^\d{4}-\d{2}-\d{2}$/.test(date);
  // Midi pour neutraliser tout décalage de fuseau qui ferait glisser d'un jour.
  const d = new Date(`${hasDay ? date : `${date}-01`}T12:00:00`);

  return d.toLocaleDateString('fr-FR', {
    ...(hasDay ? { day: 'numeric' as const } : {}),
    month: 'long',
    year: 'numeric',
  });
}

export function getSchoolYear(date: string | null): string | null {
  if (!date) return null;
  const d = new Date(/^\d{4}-\d{2}$/.test(date) ? `${date}-01` : date);
  const year = d.getFullYear();
  // Août sert de bascule : tout ce qui précède appartient à l'année entamée
  // l'année civile précédente.
  return d.getMonth() >= 7 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
}

function hydrate(album: Album): AlbumWithPhotos {
  const albumPhotos = photos.filter((p) => p.album === album.slug);
  const coverPhoto =
    albumPhotos.find((p) => p.id === album.cover) ?? albumPhotos[0];

  return {
    ...album,
    photos: albumPhotos,
    coverPhoto,
    schoolYear: getSchoolYear(album.date) ?? album.schoolYear ?? null,
  };
}

/** Albums non vides, les plus récents d'abord ; les non datés en dernier. */
export function getAllAlbums(): AlbumWithPhotos[] {
  return albums
    .map(hydrate)
    .filter((album) => album.photos.length > 0)
    .sort((a, b) => {
      if (a.date && b.date) return b.date.localeCompare(a.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return 0;
    });
}

export function getAlbumBySlug(slug: string): AlbumWithPhotos | undefined {
  const album = albums.find((a) => a.slug === slug);
  return album ? hydrate(album) : undefined;
}

/**
 * Années scolaires disponibles pour le filtre, les plus récentes d'abord.
 * Le filtre n'a de sens qu'à partir de deux années : en deçà, l'appelant
 * doit le masquer.
 */
export function getSchoolYears(): string[] {
  const years = new Set<string>();
  for (const album of getAllAlbums()) {
    if (album.schoolYear) years.add(album.schoolYear);
  }
  return [...years].sort((a, b) => b.localeCompare(a));
}
